import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { PlenaLogo } from "../components/PlenaLogo";
import { Lock, KeyRound } from "lucide-react";

const inputCls = "mt-1.5 w-full h-11 px-4 rounded-xl bg-slate-100/80 dark:bg-white/10 border border-slate-200/60 dark:border-white/10 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:bg-white dark:focus:bg-white/15 transition-all";

export default function SetPassword() {
  const { token: pathToken } = useParams();
  const [token] = useState(() => pathToken || window.location.hash.slice(1));
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    if (!token) { setError("Bağlantı geçersiz"); return; }
    api.post("/auth/token-info", { token }).then((r) => setInfo(r.data)).catch((err) => setError(formatApiError(err.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("Şifreler eşleşmiyor"); return; }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/set-password", { token, password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const isInvite = info?.kind === "invite";

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#161618] flex flex-col items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#007AFF]/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#34C759]/10 blur-3xl" />
      </div>
      <div className="relative w-full max-w-sm animate-fade-up">
        <div className="text-center mb-10">
          <div className="mb-7 flex justify-center"><PlenaLogo size="lg" /></div>
          {info ? (
            <>
              <h1 data-testid="set-password-title" className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white font-display">
                {isInvite ? `Hoş geldiniz, ${info.name.split(" ")[0]}` : "Yeni şifre belirleyin"}
              </h1>
              <p className="text-sm text-slate-500 mt-2">
                {isInvite ? <>{info.customer_name ? <><b>{info.customer_name}</b> projeleri için</> : "Plena yönetim paneli için"} hesabınızı aktifleştirin</> : info.email}
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white font-display">{error ? "Bağlantı geçersiz" : "Yükleniyor…"}</h1>
          )}
        </div>
        {info ? (
          <form onSubmit={submit} className="bg-white/85 dark:bg-[#1C1C1E]/85 backdrop-blur-2xl rounded-2xl border border-white/60 dark:border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] p-8 space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">E-posta</label>
              <input disabled value={info.email} className={`${inputCls} opacity-70`} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Şifre</label>
              <input data-testid="set-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="En az 8 karakter, harf + rakam" className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Şifre (tekrar)</label>
              <input data-testid="set-password-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            {error && <p data-testid="set-password-error" className="text-sm text-[#FF2D55] bg-[#FF2D55]/5 rounded-lg px-3 py-2">{error}</p>}
            <button data-testid="set-password-submit-button" type="submit" disabled={loading}
              className="w-full h-11 rounded-xl bg-[#0066CC] hover:bg-[#0052A3] active:scale-[0.98] text-white text-sm font-semibold transition-all shadow-lg shadow-[#0066CC]/25 disabled:opacity-60 flex items-center justify-center gap-2">
              {loading ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <KeyRound size={14} />}
              {isInvite ? "Hesabımı Aktifleştir" : "Şifreyi Güncelle"}
            </button>
          </form>
        ) : error ? (
          <div className="bg-white/85 dark:bg-[#1C1C1E]/85 backdrop-blur-2xl rounded-2xl border border-white/60 dark:border-white/10 p-8 text-center space-y-4">
            <p data-testid="set-password-error" className="text-sm text-[#FF2D55]">{error}</p>
            <button data-testid="set-password-forgot-link" onClick={() => navigate("/forgot-password")} className="text-sm font-semibold text-[#0066CC] hover:underline">Yeni bağlantı iste</button>
          </div>
        ) : null}
        <p className="text-center text-xs text-slate-400 mt-8 flex items-center justify-center gap-1.5"><Lock size={11} /> Plena.pro tarafından yönetilen güvenli alan</p>
      </div>
    </div>
  );
}
